import { AreaPageListingCardData } from 'components/pages/area-listings/area_page_listing_card_data';
import { AreaPageListingViewModel } from 'components/pages/area-listings/area-page-listing-view-model';
import { SavedListingViewModel } from 'components/account/my-zoocasa/favourites/saved-listing-view-model';
import { AreaPageListing } from '@zoocasa/go-search';
import { shouldScrambleForProvider } from 'utils/compliance';
import type { SavedListingFull } from 'contexts/saved-listing';
import type {
  ComplianceResolver,
  ListingCardDataWithCompliance,
  ExpListingCardDataWithCompliance,
} from 'types/listing-card';
import { getHideSquareFootage } from 'hooks/use-listings-compliance';

// Re-export for backward compatibility
export type { ComplianceResolver, ListingCardDataWithCompliance } from 'types/listing-card';

/**
 * Transform raw listings to v2 format for client-side use (without compliance).
 * Use with `useListingsCompliance` hook to add reactive compliance.
 */
export function toListingCardData(rawListings: AreaPageListing[]): ReturnType<AreaPageListingViewModel['toJSON']>[] {
  return rawListings.map(listing => {
    const viewModel = new AreaPageListingViewModel(listing);
    return viewModel.toJSON();
  });
}

/**
 * Transform saved listings to v2 format for client-side use (without compliance).
 * Use with `useListingsCompliance` hook to add reactive compliance.
 */
export function toSavedListingCardData(savedListings: SavedListingFull[]): Array<ReturnType<SavedListingViewModel['toJSON']> | null> {
  return savedListings.map(savedListing => {
    const { listing } = savedListing;
    if (!listing) return null;

    const viewModel = new SavedListingViewModel(listing);
    return viewModel.toJSON();
  });
}

/**
 * Transform raw listings to v2 format with compliance for SSR.
 * Returns both legacy and v2 formats for gradual migration.
 */
export function toListingCardDataWithCompliance(
  rawListings: AreaPageListing[],
  getComplianceRequirement: ComplianceResolver
): {
    legacyListings: AreaPageListingCardData[];
    listingsWithCompliance: ListingCardDataWithCompliance[];
  } {
  const legacyListings = rawListings.map(l => new AreaPageListingCardData(l));
  
  const listingsWithCompliance = rawListings.map(listing => {
    const viewModel = new AreaPageListingViewModel(listing);
    const complianceRequirement = getComplianceRequirement(listing.providerId);
    const shouldScramble = shouldScrambleForProvider(listing.providerId, complianceRequirement);

    return {
      ...viewModel.toJSON(),
      compliance: {
        complianceRequirement,
        shouldScramble,
      },
    };
  });
  
  return { legacyListings, listingsWithCompliance };
}

/**
 * Transform raw listings to legacy EXP format with compliance for SSR.
 * Uses legacy ListingCardData format (imageUrl, addressPathWithFallback, etc.).
 */
export function toExpListingCardDataWithCompliance(
  rawListings: AreaPageListing[],
  getComplianceRequirement: ComplianceResolver
): ExpListingCardDataWithCompliance[] {
  return rawListings.map(listing => {
    const complianceRequirement = getComplianceRequirement(listing.providerId);
    const shouldScramble = shouldScrambleForProvider(listing.providerId, complianceRequirement);
    
    const viewModel = new AreaPageListingCardData(listing);
    
    return {
      ...viewModel.toJSON(listing),
      compliance: {
        complianceRequirement,
        shouldScramble,
      },
    } as ExpListingCardDataWithCompliance;
  });
}

/**
 * Transform saved listings to v2 format with compliance for SSR.
 * Returns null for deleted listings (component handles placeholder UI).
 */
export function toSavedListingCardDataWithCompliance(
  savedListings: SavedListingFull[],
  getComplianceRequirement: ComplianceResolver
): Array<ListingCardDataWithCompliance | null> {
  return savedListings.map(savedListing => {
    const { listing } = savedListing;
    if (!listing) return null;

    const viewModel = new SavedListingViewModel(listing);
    const complianceRequirement = getComplianceRequirement(listing.providerId ?? 0);
    const shouldScramble = shouldScrambleForProvider(listing.providerId ?? 0, complianceRequirement);

    const hideSquareFootage = getHideSquareFootage(listing.providerId, listing.standardStatus);

    return {
      ...viewModel.toJSON(),
      compliance: {
        complianceRequirement,
        shouldScramble,
        hideSquareFootage,
      },
    };
  });
}
